// Canvas atlases the shop samples from.
//
// Three atlases live here:
//
//   * the generic memoised `buildAtlas()` sheet, used for the poster and price
//     card textures (each cell is one already-painted procedural texture);
//   * the 16x16 / 128px album cover atlas, whose 256 cells are partitioned into
//     front-facing covers (0-111), per-genre spine/hero covers (112-175) and a
//     pool of dig slots (176-247) handed out LRU as the player opens racks;
//   * the divider-label / shop-sign atlas, 8 columns of 256x96 tiles with
//     auto-shrinking text.
//
// `atlasQuad(atlas, idx, w, h)` cuts a UV'd quad out of any of them.
//
// Cover cells are painted asynchronously from <img> loads. Each cell carries a
// token in CELL_TOKEN that is bumped on every repaint, so a load that resolves
// after the cell has been reassigned is dropped. CELL_RECORD holds whatever
// record is currently painted into each cell.
//
// A cell is filled twice where it can be: COVER_FALLBACKS holds an inline copy
// of 256 of the 1,120 covers, at 128px, which is exactly one cell, and it needs
// no network. That goes in as soon as it decodes and the 384px network copy
// replaces it when it arrives.
//
// Recovered from the production bundle.

import * as THREE from 'three';
import { thumbUrl, bays as genreTree, seededShuffle } from './catalogue.js';
import { COVER_FALLBACKS } from './coverFallbacks.js';
import { SLEEVES } from './store.js';
import { posterTexture, POSTER_COUNT, cardTexture, CARD_COUNT, rnd } from './textures.js';

// ---------------------------------------------------------------------------
// Generic memoised atlas: draws `count` already-built textures into a grid
// ---------------------------------------------------------------------------

function buildAtlas(key, cols, rows, cellW, cellH, count, makeTexture) {
    if (buildAtlas._cache[key]) return buildAtlas._cache[key];
    const canvas = document.createElement("canvas");
    canvas.width = cols * cellW;
    canvas.height = rows * cellH;
    const ctx = canvas.getContext("2d");
    for (let i = 0; i < count; i++) {
        const image = makeTexture(i).image;
        ctx.drawImage(image, i % cols * cellW, Math.floor(i / cols) * cellH, cellW, cellH);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const atlas = {
        tex: tex,
        cols: cols,
        rows: rows
    };
    buildAtlas._cache[key] = atlas;
    return atlas;
}
buildAtlas._cache = {};

const posterAtlas = () => buildAtlas("poster", 8, 6, 256, 384, POSTER_COUNT, posterTexture);
const cardAtlas = () => buildAtlas("card", 4, 4, 256, 160, CARD_COUNT, cardTexture);

// A PlaneGeometry whose UVs are remapped onto cell `idx` of `atlas`. Faces
// local +z.
function atlasQuad(atlas, idx, width, height) {
    const geo = new THREE.PlaneGeometry(width, height);
    const tile = atlas.tiles && atlas.tiles[idx];
    if (tile && tile.strip) {
        // Lettering strip: show the window of the strip that has the quad's own
        // aspect, centred on the text, so the letters are never stretched.
        const win = stripWindow(tile, width / height),
            uv = geo.attributes.uv,
            cx = atlas.width / 2,
            cy = tile.strip.y + tile.strip.h / 2;
        for (let i = 0; i < uv.count; i++) {
            uv.setXY(i, (cx + (uv.getX(i) - .5) * win.w) / atlas.width, 1 - (cy - (uv.getY(i) - .5) * win.h) / atlas.height);
        }
        uv.needsUpdate = !0;
        return geo;
    }
    const gridFrac = atlas.gridFrac ?? 1,
        col = idx % atlas.cols;
    const row = Math.floor(idx / atlas.cols) % atlas.rows;
    const uv = geo.attributes.uv;
    for (let i = 0; i < uv.count; i++) {
        // The grid fills the top `gridFrac` of a sheet that has strips below it.
        const v = (atlas.rows - row - 1 + uv.getY(i)) / atlas.rows;
        uv.setXY(i, (col + uv.getX(i)) / atlas.cols, 1 - gridFrac + v * gridFrac);
    }
    uv.needsUpdate = !0;
    return geo;
}

// ---------------------------------------------------------------------------
// Album cover atlas
// ---------------------------------------------------------------------------

const GENRE_CELLS = 4,
    GENRE_CELL_CHOICES = GENRE_CELLS,
    ATLAS_COLS = 16,
    ATLAS_ROWS = 16,
    ATLAS_CELL = 128,
    FRONT_CELL_START = 0,
    FRONT_CELL_CAP = 112,
    GENRE_CELL_START = 112,
    DIG_CELL_START = 176,
    DIG_CELLS_PER_SLOT = 12,
    DIG_SLOTS = 6,
    GENRE_CELL_BASE = {},
    GENRE_HERO_RECORDS = {},
    GENRE_COVER_TINTS = {};

let _coverAtlasCache = null,
    coverCtx = null,
    coverAtlasDirty = !1;

const SPINE_MESHES = [],
    CELL_RECORD = new Array(ATLAS_COLS * ATLAS_ROWS).fill(null),
    CELL_TOKEN = new Uint16Array(ATLAS_COLS * ATLAS_ROWS),
    CELL_BLANK = "#8a7a60";

function cellOrigin(cell) {
    return [cell % ATLAS_COLS * ATLAS_CELL, Math.floor(cell / ATLAS_COLS) * ATLAS_CELL];
}

function markAtlasDirty() {
    coverAtlasDirty = !0;
}

function flushAtlas() {
    if (!coverAtlasDirty || !_coverAtlasCache) return;
    coverAtlasDirty = !1;
    _coverAtlasCache.tex.needsUpdate = !0;
}

// Fetches a record's thumbnail over the network.
function loadCover(record, onLoad) {
    const img = new Image;
    img.onload = () => onLoad(img);
    img.src = thumbUrl(record);
    return img;
}

// Fills a cell with a record's cover. The inline copy, where the record has
// one, is drawn as soon as it decodes; the network copy replaces it when it
// lands. `done(alreadyPainted, img)` fires on each of those, so a caller that
// only wants the cell to become usable can act on the first.
function paintCell(cell, record, done) {
    if (CELL_RECORD[cell] && CELL_RECORD[cell].id === record.id) {
        done && done(!0);
        return;
    }
    const token = ++CELL_TOKEN[cell];
    CELL_RECORD[cell] = null;
    const [x, y] = cellOrigin(cell);
    coverCtx.fillStyle = CELL_BLANK;
    coverCtx.fillRect(x, y, ATLAS_CELL, ATLAS_CELL);
    markAtlasDirty();
    // Ranked so a slow inline decode cannot land on top of a network copy that
    // already beat it into the cell.
    let rank = 0;
    const fill = (img, level) => {
        if (CELL_TOKEN[cell] !== token || level <= rank) return;
        rank = level;
        coverCtx.drawImage(img, x, y, ATLAS_CELL, ATLAS_CELL);
        CELL_RECORD[cell] = record;
        markAtlasDirty();
        done && done(!1, img);
    };
    const inlineCover = COVER_FALLBACKS[record.thumb];
    if (inlineCover) {
        const stand = new Image;
        stand.onload = () => fill(stand, 1);
        stand.src = inlineCover;
    }
    loadCover(record, img => fill(img, 2));
}

function coverAtlas() {
    if (_coverAtlasCache) return _coverAtlasCache;
    const canvas = document.createElement("canvas");
    canvas.width = ATLAS_COLS * ATLAS_CELL;
    canvas.height = ATLAS_ROWS * ATLAS_CELL;
    coverCtx = canvas.getContext("2d");
    coverCtx.fillStyle = CELL_BLANK;
    coverCtx.fillRect(0, 0, canvas.width, canvas.height);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    _coverAtlasCache = {
        tex: tex,
        cols: ATLAS_COLS,
        rows: ATLAS_ROWS
    };
    // 4x4 scratch canvas: each loaded cover is downsampled into it and averaged
    // to give the genre its spine tint.
    const sampleCanvas = document.createElement("canvas");
    sampleCanvas.width = 4;
    sampleCanvas.height = 4;
    const sampleCtx = sampleCanvas.getContext("2d", {
            willReadFrequently: !0
        }),
        tree = genreTree(),
        expected = 16 * GENRE_CELLS;
    // A cell reports in twice when it has an inline copy as well as a network
    // one, so count cells rather than callbacks. Spines are tinted once every
    // hero cell has art, and once at 8s from whatever has arrived by then; a
    // later full set supersedes that, nothing supersedes the full set.
    const filled = new Set();
    let tintedFrom = 0;
    const tint = level => {
        if (level <= tintedFrom) return;
        tintedFrom = level;
        tintSpines();
    };
    tree.forEach((genre, genreIndex) => {
        GENRE_CELL_BASE[genre.genreId] = GENRE_CELL_START + genreIndex * GENRE_CELLS;
        GENRE_COVER_TINTS[genre.genreId] = [];
        const pool = [];
        genre.subgenres.forEach(sub => pool.push(...sub.records));
        const heroes = seededShuffle(pool, 7 + genreIndex * 13).slice(0, GENRE_CELLS);
        GENRE_HERO_RECORDS[genre.genreId] = heroes;
        heroes.forEach((record, heroIndex) => {
            const cell = GENRE_CELL_BASE[genre.genreId] + heroIndex;
            paintCell(cell, record, (alreadyPainted, img) => {
                if (img) try {
                    sampleCtx.drawImage(img, 0, 0, 4, 4);
                    const px = sampleCtx.getImageData(0, 0, 4, 4).data;
                    let red = 0,
                        green = 0,
                        blue = 0;
                    for (let k = 0; k < 16; k++) {
                        red += px[k * 4];
                        green += px[k * 4 + 1];
                        blue += px[k * 4 + 2];
                    }
                    GENRE_COVER_TINTS[genre.genreId][heroIndex] = new THREE.Color(red / 16 / 255, green / 16 / 255, blue / 16 / 255);
                } catch {}
                filled.add(cell);
                if (filled.size >= expected) tint(2);
            });
        });
    });
    setTimeout(() => tint(1), 8e3);
    return _coverAtlasCache;
}

// Recolours every registered spine InstancedMesh from the averaged cover tints.
function tintSpines() {
    const color = new THREE.Color;
    for (const mesh of SPINE_MESHES) {
        const genres = mesh.userData.genres;
        if (!genres || !mesh.instanceColor) continue;
        for (let i = 0; i < genres.length; i++) {
            const tints = GENRE_COVER_TINTS[genres[i]];
            if (!tints || !tints.length) continue;
            const tint = tints[(i * 7 + 3) % tints.length];
            if (!tint) continue;
            color.copy(tint);
            const hsl = {
                h: 0,
                s: 0,
                l: 0
            };
            color.getHSL(hsl);
            color.setHSL(hsl.h, Math.min(.8, hsl.s * .85), .1 + hsl.l * .42);
            mesh.setColorAt(i, color);
        }
        mesh.instanceColor.needsUpdate = !0;
    }
}

const genreCell = (genreId, index) => (GENRE_CELL_BASE[genreId] ?? GENRE_CELL_START) + (index % GENRE_CELLS + GENRE_CELLS) % GENRE_CELLS;

let frontCellNext = 0;
const DIG_SLOT_POOL = [];
let digStamp = 1;

// Bump allocator over the front-facing cell range; -1 once it is exhausted.
function assignFrontCell(rack) {
    coverAtlas();
    if (frontCellNext >= FRONT_CELL_CAP) return -1;
    const cell = FRONT_CELL_START + frontCellNext++;
    rack.frontCell = cell;
    paintCell(cell, rack.records[0]);
    return cell;
}

// Claims a dig slot for `rack`, evicting the least recently stamped slot when
// the pool is full, then repoints the rack's sleeve instances at those cells.
function ensureDigArt(rack) {
    if (rack && rack.raw) rack = rack.raw;
    if (!rack || !Array.isArray(rack.sleeves) || !rack.sleeves.length || !Array.isArray(rack.records)) return null;
    coverAtlas();
    let slot = DIG_SLOT_POOL.find(s => s.rack === rack);
    const claimed = !slot;
    if (!slot) {
        if (DIG_SLOT_POOL.length < DIG_SLOTS) {
            slot = {
                base: DIG_CELL_START + DIG_SLOT_POOL.length * DIG_CELLS_PER_SLOT,
                rack: null,
                stamp: 0
            };
            DIG_SLOT_POOL.push(slot);
        } else {
            slot = DIG_SLOT_POOL.reduce((a, b) => a.stamp <= b.stamp ? a : b);
            if (slot.rack) releaseDigArt(slot.rack);
        }
        slot.rack = rack;
    }
    slot.stamp = ++digStamp;
    rack.digSlot = slot;
    if (claimed) rack.records.forEach((record, i) => {
        if (i >= DIG_CELLS_PER_SLOT) return;
        paintCell(slot.base + i, record, () => showDigCell(rack, slot, i));
    });
    rack.sleeves.forEach((sleeve, i) => {
        if (i >= DIG_CELLS_PER_SLOT) return;
        showDigCell(rack, slot, i);
    });
    return slot;
}

// Moves one sleeve onto its dig cell, but only once that cell holds the record
// the sleeve is. A cell is blank between being claimed and its cover arriving,
// so until then the sleeve stays on the genre cover it is already showing.
function showDigCell(rack, slot, i) {
    if (rack.digSlot !== slot) return;
    const sleeve = rack.sleeves[i];
    if (!sleeve || !sleeve.userData) return;
    if (i === 0 && rack.frontCell >= 0) {
        setSleeveTile(sleeve.userData.instIndex, rack.frontCell);
        return;
    }
    const cell = slot.base + i,
        record = rack.records[i];
    if (record && CELL_RECORD[cell] && CELL_RECORD[cell].id === record.id) setSleeveTile(sleeve.userData.instIndex, cell);
}

// Sends a rack's sleeves back to their genre cells.
function releaseDigArt(rack) {
    if (!rack || !Array.isArray(rack.sleeves)) return;
    rack.digSlot = null;
    rack.sleeves.forEach((sleeve, i) => {
        setSleeveTile(sleeve.userData.instIndex, i === 0 && rack.frontCell >= 0 ? rack.frontCell : genreCell(rack.genreId, i));
    });
}

function setSleeveTile(instIndex, cell) {
    if (instIndex == null) return;
    SLEEVES.tile[instIndex] = cell;
    const aTile = SLEEVES.mesh && SLEEVES.mesh.geometry.attributes.aTile;
    if (!aTile) return;
    aTile.array[instIndex * 2] = cell % ATLAS_COLS / ATLAS_COLS;
    aTile.array[instIndex * 2 + 1] = (ATLAS_ROWS - Math.floor(cell / ATLAS_COLS) - 1) / ATLAS_ROWS;
    aTile.needsUpdate = !0;
}

// Reports whether the art painted into a sleeve's cell is the record that
// sleeve claims to be.
function sleeveArtDebug(sleeve) {
    const instIndex = sleeve?.userData?.instIndex,
        cell = instIndex == null ? -1 : SLEEVES.tile[instIndex],
        painted = cell >= 0 ? CELL_RECORD[cell] : null,
        record = sleeve?.userData?.record;
    return {
        cell: cell,
        paintedId: painted ? painted.id : null,
        paintedTitle: painted ? `${painted.artist} — ${painted.title}` : null,
        recordId: record ? record.id : null,
        recordTitle: record ? `${record.artist} — ${record.title}` : null,
        ok: !!painted && !!record && painted.id === record.id
    };
}

// ---------------------------------------------------------------------------
// Divider-label / shop-sign atlas
// ---------------------------------------------------------------------------

const LABEL_TILES = [];
let BLANK_LABEL_TILE = 0;
const LABEL_TILE_INDEX = {};
let _labelAtlasCache = null;

function labelAtlas() {
    if (_labelAtlasCache) return _labelAtlasCache;
    const cols = 8,
        cellW = 256,
        cellH = 96;
    // Each genre takes a head tile, then one tile per subgenre, then is padded
    // out to a whole row of 8.
    for (const genre of genreTree()) {
        LABEL_TILE_INDEX[genre.genreId] = LABEL_TILES.length;
        LABEL_TILES.push({
            text: genre.label.toUpperCase(),
            head: !0
        });
        for (const sub of genre.subgenres) {
            LABEL_TILE_INDEX[genre.genreId + "/" + sub.id] = LABEL_TILES.length;
            LABEL_TILES.push({
                text: sub.label,
                head: !1
            });
        }
        for (; LABEL_TILES.length % 8 !== 0;) LABEL_TILES.push({
            text: "",
            head: !1
        });
    }
    SIGN_PHRASES.forEach(phrase => {
        SIGN_TILE_INDEX[phrase] = LABEL_TILES.length;
        LABEL_TILES.push({
            text: phrase,
            sign: !0
        });
    });
    WIDE_SIGN_PHRASES.forEach(phrase => {
        SIGN_TILE_INDEX[phrase] = LABEL_TILES.length;
        LABEL_TILES.push({
            text: phrase,
            sign: !0,
            wide: !0
        });
    });
    BLANK_LABEL_TILE = LABEL_TILES.length;
    LABEL_TILES.push({
        text: "",
        blank: !0
    });
    const rows = Math.ceil(LABEL_TILES.length / cols),
        gridH = rows * cellH,
        canvas = document.createElement("canvas");
    canvas.width = cols * cellW;
    // Lettering strips sit under the grid, each as wide as the whole sheet.
    const stripTiles = LABEL_TILES.filter(tile => tile.wide && STRIP_PHRASES.includes(tile.text));
    let stripY = gridH;
    for (const tile of stripTiles) {
        measureStrip(tile, canvas.width);
        tile.strip.y = stripY;
        stripY += tile.strip.h;
    }
    canvas.height = stripY;
    const ctx = canvas.getContext("2d");
    LABEL_TILES.forEach((tile, index) => {
        if (tile.strip) return;
        const x = index % cols * cellW,
            y = Math.floor(index / cols) * cellH;
        ctx.save();
        ctx.translate(x, y);
        if (tile.blank) ctx.fillStyle = "#e8e4da";
        else if (tile.sign) ctx.fillStyle = "#e6ddc8";
        else if (tile.head) ctx.fillStyle = "#e2d3ad";
        else ctx.fillStyle = "#ded3ba";
        ctx.fillRect(0, 0, cellW, cellH);
        ctx.fillStyle = "rgba(90,70,40,0.13)";
        for (let i = 0; i < 7; i++) ctx.fillRect(rnd() * cellW, rnd() * cellH, 20 + rnd() * 60, 3 + rnd() * 8);
        if (!tile.text) {
            ctx.restore();
            return;
        }
        ctx.translate(cellW / 2, cellH / 2 + (tile.head ? 4 : 2));
        if (!tile.wide) ctx.rotate((rnd() - .5) * .045);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const weight = tile.head || tile.sign ? "700" : "600",
            family = '"Trebuchet MS", "Segoe UI", sans-serif';
        let size;
        if (tile.wide) size = 42;
        else if (tile.sign) size = 50;
        else if (tile.head) size = 54;
        else size = 46;
        const minSize = 24,
            maxWidth = cellW - 26;
        let text = tile.text;
        for (ctx.font = `${weight} ${size}px ${family}`; ctx.measureText(text).width > maxWidth && size > minSize;) {
            size -= 1;
            ctx.font = `${weight} ${size}px ${family}`;
        }
        if (ctx.measureText(text).width > maxWidth) {
            for (; text.length > 1 && ctx.measureText(text + "…").width > maxWidth;) text = text.slice(0, -1);
            text += "…";
        }
        tile.fit = Math.min(1, (ctx.measureText(text).width + 26) / cellW);
        tile.size = size;
        if (tile.sign) ctx.fillStyle = "#8e1c17";
        else if (tile.head) ctx.fillStyle = "#241a10";
        else ctx.fillStyle = "#2b1f14";
        ctx.fillText(text, 0, 0);
        if (!tile.wide) {
            ctx.globalAlpha = .5;
            ctx.fillText(text, 1.4, .8);
            ctx.globalAlpha = 1;
        }
        if (tile.head) {
            // hand-drawn underline: 6 segments jittered off the baseline
            ctx.strokeStyle = "#241a10";
            ctx.lineWidth = 3.5;
            ctx.beginPath();
            const ruleWidth = Math.min(cellW - 30, ctx.measureText(text).width + 12);
            ctx.moveTo(-ruleWidth / 2, size * .52);
            for (let i = 0; i <= 6; i++) ctx.lineTo(-ruleWidth / 2 + ruleWidth / 6 * i, size * .52 + (rnd() - .5) * 4);
            ctx.stroke();
        }
        ctx.restore();
    });
    stripTiles.forEach(tile => paintStrip(ctx, tile, canvas.width));
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 16;
    _labelAtlasCache = {
        tex: tex,
        cols: cols,
        rows: rows,
        width: canvas.width,
        height: canvas.height,
        gridFrac: gridH / canvas.height,
        tiles: LABEL_TILES
    };
    return _labelAtlasCache;
}

// Wide phrases that get a full-width lettering strip. These are the ones the
// fixtures place on boards from 2.7:1 (the entrance sign) to 11:1 (the counter
// fascia); the other wide phrases stay in the grid.
const STRIP_PHRASES = ["NSPIRE RECORDS", "INFORMATION", "PAY HERE", "CHECK OUT"],
    STRIP_FONT = '"Arial Narrow", "Liberation Sans Narrow", "DejaVu Sans Condensed", "Trebuchet MS", sans-serif',
    STRIP_SQUEEZE = .8,
    STRIP_SIZE = 140,
    STRIP_SPACING = 7,
    STRIP_MAX_TEXT = 1100,
    STRIP_MARGIN = 1.3,
    STRIP_MIN_ASPECT = 2.6;

// Sets the font on `ctx` for a strip tile and returns the painted text width.
// The face is squeezed horizontally by STRIP_SQUEEZE for a condensed enamel
// look, and the size shrinks until the text is no wider than STRIP_MAX_TEXT.
function stripFont(ctx, tile) {
    let size = STRIP_SIZE;
    for (;;) {
        ctx.font = `800 ${size}px ${STRIP_FONT}`;
        ctx.letterSpacing = size * STRIP_SPACING / STRIP_SIZE + "px";
        const width = ctx.measureText(tile.text).width * STRIP_SQUEEZE;
        if (width <= STRIP_MAX_TEXT || size <= 40) return {
            size,
            width
        };
        size -= 2;
    }
}

// Works out a strip tile's text size, its slot height (tall enough that the
// quad aspect can fall as low as STRIP_MIN_ASPECT) and its fit.
function measureStrip(tile, canvasW) {
    const scratch = document.createElement("canvas").getContext("2d"),
        {
            size,
            width
        } = stripFont(scratch, tile),
        capH = size * .72,
        windowW = width + capH * STRIP_MARGIN;
    tile.strip = {
        y: 0,
        h: Math.ceil(Math.max(capH * 1.9, windowW / STRIP_MIN_ASPECT) / 8) * 8,
        size,
        textW: width,
        winW: windowW,
        capH
    };
    tile.size = size;
    tile.fit = windowW / canvasW;
}

// The window of a strip, in strip pixels, for a quad of aspect `aspect`: the
// text plus its margin fills the width, unless that would make the window
// shorter than the lettering, and it never grows taller than the slot.
function stripWindow(tile, aspect) {
    const s = tile.strip;
    let h = s.winW / aspect;
    const minH = s.capH * 1.5;
    if (h < minH) h = minH;
    if (h > s.h) h = s.h;
    return {
        w: h * aspect,
        h: h
    };
}

// Flat oxblood enamel lettering on cream, centred in the tile's slot.
function paintStrip(ctx, tile, canvasW) {
    const s = tile.strip;
    ctx.save();
    ctx.fillStyle = "#e6ddc8";
    ctx.fillRect(0, s.y, canvasW, s.h);
    ctx.fillStyle = "rgba(90,70,40,0.08)";
    for (let i = 0; i < 40; i++) ctx.fillRect(rnd() * canvasW, s.y + rnd() * s.h, 40 + rnd() * 200, 4 + rnd() * 14);
    stripFont(ctx, tile);
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#8e1c17";
    ctx.translate(canvasW / 2, s.y + s.h / 2 + s.capH / 2);
    ctx.scale(STRIP_SQUEEZE, 1);
    // letterSpacing trails the last glyph, so shift back by half of it
    ctx.fillText(tile.text, parseFloat(ctx.letterSpacing) / 2, 0);
    ctx.restore();
}

// Fraction of a tile's width the painted text actually occupies, floored at
// .42; used to narrow the quad a label is drawn on.
function labelFit(tileIndex) {
    labelAtlas();
    const tile = LABEL_TILES[tileIndex];
    return tile && tile.fit ? Math.max(.42, tile.fit) : 1;
}

const SIGN_PHRASES = ["NEW ARRIVALS", "USED LP £5", "50p EACH", "SALE", '7" SINGLES', "A – Z", "STAFF PICKS", "NO SLEEVE", "PROMO ONLY", "WHITE LABEL", "JUST IN", "£1 BIN", "IMPORTS", "SECONDHAND", "ASK AT TILL", "PLEASE FLICK GENTLY"],
    WIDE_SIGN_PHRASES = ["NSPIRE RECORDS", "INFORMATION", "PAY HERE", "TILL", "CASH · CARD", "THANK YOU", "NSPIRE", "ASK HERE", "CHECK OUT"],
    SIGN_TILE_INDEX = {},
    SPINE_PALETTE = ["#cfc3a8", "#2a231b", "#8c6a44", "#b9b3a4", "#5a4636", "#d8cdb6"];

export {
    ATLAS_COLS,
    ATLAS_ROWS,
    posterAtlas,
    cardAtlas,
    atlasQuad,
    GENRE_CELL_CHOICES,
    GENRE_HERO_RECORDS,
    flushAtlas,
    coverAtlas,
    genreCell,
    assignFrontCell,
    ensureDigArt,
    sleeveArtDebug,
    SPINE_MESHES,
    labelAtlas,
    labelFit,
    BLANK_LABEL_TILE,
    LABEL_TILE_INDEX,
    SIGN_TILE_INDEX,
    SIGN_PHRASES,
    WIDE_SIGN_PHRASES,
    SPINE_PALETTE,
};
